import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

const fmt = (num) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);

export function PaymentApprovalModal({ isOpen, onClose, pendingPayment, onConfirm, bankAccounts, isConfirming }) {
  const [bankAsal, setBankAsal] = useState('');
  const [coaAllocations, setCoaAllocations] = useState([]);

  useEffect(() => {
    if (pendingPayment) {
      setBankAsal(pendingPayment.bank_key || 'bca');
      setCoaAllocations([
        {
          prefix: '1',
          account_id: '',
          account_name: '',
          amount: pendingPayment.jumlah || 0
        }
      ]);
    }
  }, [pendingPayment]);

  const { data: coaList = [] } = useQuery({
    queryKey: ['coa-list'],
    queryFn: () => base44.entities.ChartOfAccount.list('id')
  });

  if (!pendingPayment) return null;

  const totalAlokasi = coaAllocations.reduce((s, c) => s + Number(c.amount || 0), 0);
  const isValid = totalAlokasi === (pendingPayment.jumlah || 0) && coaAllocations.every(c => c.account_id);

  const handleConfirm = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onConfirm({
      bank_tujuan: bankAsal,
      coa_allocations: coaAllocations
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white rounded-2xl border-none shadow-2xl">
        <DialogHeader className="px-6 py-4 bg-slate-50/80 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            Konfirmasi Uang Masuk
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleConfirm} className="p-6 space-y-5">
          <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3 flex justify-between items-center">
            <div>
              <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">{pendingPayment.invoice_name}</p>
              <p className="text-xs text-blue-600 mt-1">Oleh: {pendingPayment.diinput_oleh}</p>
            </div>
            <p className="text-base font-black text-blue-700">Rp {fmt(pendingPayment.jumlah)}</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-700">Terima di Rekening</Label>
            <Select value={bankAsal} onValueChange={setBankAsal}>
              <SelectTrigger className="h-10 text-xs bg-white border-slate-200">
                <SelectValue placeholder="Pilih Bank" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {Object.keys(bankAccounts || {}).map(key => (
                  <SelectItem key={key} value={key}>
                    {bankAccounts[key]?.name || key.toUpperCase()} ({(bankAccounts[key]?.nama_bank || key).toUpperCase()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 border border-slate-200 rounded-xl p-3 bg-slate-50/30">
            <div className="flex justify-between items-center">
              <Label className="text-xs font-bold text-slate-700">Alokasi Akun COA (Wajib)</Label>
              <span className={`text-[10px] font-bold ${totalAlokasi !== pendingPayment.jumlah ? 'text-rose-500' : 'text-emerald-600'}`}>
                Total: Rp {fmt(totalAlokasi)}
              </span>
            </div>
            
            {coaAllocations.map((coa, idx) => (
              <div key={idx} className="flex gap-2 items-start relative group">
                <div className="flex-1 grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Select 
                      value={coa.prefix} 
                      onValueChange={val => {
                        const newAlloc = [...coaAllocations];
                        newAlloc[idx].prefix = val;
                        newAlloc[idx].account_id = '';
                        newAlloc[idx].account_name = '';
                        setCoaAllocations(newAlloc);
                      }}
                    >
                      <SelectTrigger className="h-9 text-[11px] bg-white border-slate-200">
                        <SelectValue placeholder="Kategori" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="1">1 - Aset</SelectItem>
                        <SelectItem value="2">2 - Kewajiban</SelectItem>
                        <SelectItem value="3">3 - Ekuitas</SelectItem>
                        <SelectItem value="4">4 - Pendapatan</SelectItem>
                        <SelectItem value="5">5 - HPP</SelectItem>
                        <SelectItem value="6">6 - Beban</SelectItem>
                        <SelectItem value="7">7 - Pdt Lain</SelectItem>
                        <SelectItem value="8">8 - Bbn Lain</SelectItem>
                        <SelectItem value="9">9 - Pajak</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-8">
                    <Select 
                      value={coa.account_id} 
                      onValueChange={val => {
                        const selectedCoa = coaList.find(c => c.id === val);
                        const newAlloc = [...coaAllocations];
                        newAlloc[idx].account_id = val;
                        newAlloc[idx].account_name = selectedCoa?.name || '';
                        setCoaAllocations(newAlloc);
                      }}
                    >
                      <SelectTrigger className="h-9 text-[11px] bg-white border-slate-200">
                        <SelectValue placeholder="Pilih Akun" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {coaList.filter(c => c.id.startsWith(coa.prefix)).map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.id} - {c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-12">
                    <Input 
                      type="number" 
                      placeholder="Nominal" 
                      value={coa.amount} 
                      onChange={e => {
                        const newAlloc = [...coaAllocations];
                        newAlloc[idx].amount = e.target.value ? Number(e.target.value) : '';
                        setCoaAllocations(newAlloc);
                      }} 
                      className="h-9 text-xs"
                    />
                  </div>
                </div>
                {coaAllocations.length > 1 && (
                  <button 
                    type="button"
                    onClick={() => setCoaAllocations(coaAllocations.filter((_, i) => i !== idx))}
                    className="mt-1 p-1 text-slate-400 hover:text-rose-500 bg-white border border-slate-200 rounded-md"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="w-full h-8 text-[10px] text-slate-500 border-dashed border-slate-300"
              onClick={() => setCoaAllocations([...coaAllocations, { prefix: '4', account_id: '', account_name: '', amount: '' }])}
            >
              <Plus className="w-3 h-3 mr-1" /> Tambah Akun COA
            </Button>
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className="h-10 text-xs px-6" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={!isValid || isConfirming} className="h-10 text-xs px-6 bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              <CheckCircle2 className="w-4 h-4" />
              {isConfirming ? 'Menyimpan...' : 'Konfirmasi Uang Masuk'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
